import React from 'react';
import { X } from 'lucide-react';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      children,
      variant = 'neutral',
      size = 'md',
      rounded = false,
      removable = false,
      onRemove,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center gap-1 font-medium transition-all duration-200';

    // Improved colors for dark mode compatibility
    const variantClasses = {
      success: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
      warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
      danger: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
      info: 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
      neutral: 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    };

    const sizeClasses = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
      lg: 'px-3 py-1.5 text-base',
    };

    const roundedClasses = rounded ? 'rounded-full' : 'rounded-md';

    // ARIA role based on variant for accessibility
    const ariaRole = variant === 'danger' ? 'alert' : variant === 'warning' || variant === 'success' ? 'status' : undefined;

    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${roundedClasses} ${className}`;

    return (
      <span ref={ref} className={classes} role={ariaRole} {...props}>
        {children}
        {removable && onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors touch-target"
            aria-label="Supprimer"
          >
            <X size={size === 'sm' ? 10 : size === 'lg' ? 14 : 12} />
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export interface BadgeDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  pulse?: boolean;
  className?: string;
}

export const BadgeDot = React.forwardRef<HTMLSpanElement, BadgeDotProps>(
  ({ variant = 'neutral', pulse = false, className = '', ...props }, ref) => {
    const variantClasses = {
      success: 'bg-green-500',
      warning: 'bg-yellow-500',
      danger: 'bg-red-500',
      info: 'bg-blue-500',
      neutral: 'bg-gray-500',
    };

    const pulseClasses = pulse ? 'animate-pulse' : '';

    return (
      <span
        ref={ref}
        className={`inline-block h-2 w-2 rounded-full ${variantClasses[variant]} ${pulseClasses} ${className}`}
        {...props}
      />
    );
  }
);

BadgeDot.displayName = 'BadgeDot';

export interface BadgeCountProps extends React.HTMLAttributes<HTMLSpanElement> {
  count: number;
  max?: number;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  showZero?: boolean;
  className?: string;
}

export const BadgeCount = React.forwardRef<HTMLSpanElement, BadgeCountProps>(
  (
    {
      count,
      max = 99,
      variant = 'danger',
      showZero = false,
      className = '',
      ...props
    },
    ref
  ) => {
    if (count === 0 && !showZero) {
      return null;
    }

    const variantClasses = {
      success: 'bg-green-500 text-white',
      warning: 'bg-yellow-500 text-white',
      danger: 'bg-red-500 text-white',
      info: 'bg-blue-500 text-white',
      neutral: 'bg-gray-500 text-white',
    };

    const displayCount = count > max ? `${max}+` : count.toString();

    return (
      <span
        ref={ref}
        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${variantClasses[variant]} ${className}`}
        {...props}
      >
        {displayCount}
      </span>
    );
  }
);

BadgeCount.displayName = 'BadgeCount';
